import React, { FC, useState } from 'react'

import { Loader2Icon, Trash2 } from 'lucide-react'
import { Button } from '../../ui/button'

import { IconCreditCard } from '../../icons/IconCreditCard'
import { IconBank } from '../../icons/IconBank'
import { SendRequest } from '../../modals/InfoModals/components/PaymentActions/components/SendRequest/SendRequest'
import { PendingRequest } from '../../modals/InfoModals/components/PaymentActions/components/PendingRequest/PendingRequest'
import { RejectedRequest } from '../../modals/InfoModals/components/PaymentActions/components/RejectedRequest/RejectedRequest'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'

import {
  IPaymentsCards,
  ISelectedCard,
  IUserACH,
  IUserCard,
  IUserInterface,
  IUserInterfaceExtended,
} from '../../../models/users/user.interface'

import { httpService } from '../../../services/httpService'
import useToasterHelper from '../../../helpers/ToasterHelper'
import { cn } from '../../../lib/utils'

import { MESSAGES_CONST } from '../../../const/messages-const'

interface PaymentCardItemProps {
  type: 'card' | 'us_bank_account'
  pmId: string
  cardNumber: string | null
  bankName: string | null
  selectedCard: ISelectedCard
  setSelectedCard: (value: ISelectedCard) => void
  paymentMethods: IPaymentsCards
  setPaymentMethods: (value: IPaymentsCards) => void
  organiserProfile: IUserInterfaceExtended | null
  isDefault: boolean
  card: IUserCard | IUserACH
  paymentRequestStatus: string | null
  user: IUserInterface | null
}
export const PaymentCardItem: FC<PaymentCardItemProps> = ({
  type,
  pmId,
  bankName,
  selectedCard,
  setSelectedCard,
  cardNumber,
  paymentMethods,
  setPaymentMethods,
  organiserProfile,
  isDefault,
  card,
  user,
  paymentRequestStatus,
}) => {
  const toastFunction = useToasterHelper()

  const [trashLoading, setTrashLoading] = useState(false)

  const onRemoveCard = async (id: string) => {
    try {
      setTrashLoading(true)
      await httpService({
        url: `remove_card`,
        method: 'POST',
        data: {
          pmId: id,
          userStripeAccountId: organiserProfile?.userStripeAccountId,
        },
      })

      const cards = paymentMethods.CARDS.filter((item) => item.pmId !== id)
      const ach = paymentMethods.ACH.filter((item) => item.pmId !== id)
      setPaymentMethods({
        CARDS: cards,
        ACH: ach,
      })
      toastFunction.success({
        message: 'Card removed successfully',
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunction.error({
        message: error?.response?.data?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setTrashLoading(false)
    }
  }

  const iconClasses = cn(
    'absolute z-10 left-4 top-1/2 -translate-y-1/2 w-[22px] group-hover:fill-SeabiscuitMainThemeColor transition-all',
    selectedCard?.pmId === pmId
      ? 'fill-SeabiscuitMainThemeColor'
      : 'fill-SeabiscuitDark200ThemeColor'
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'w-full text-sm py-4 pr-2.5 pl-14 flex items-center gap-2 flex-center relative mb-2.5 border rounded-lg focus:border-blue-500 transition-all',
            selectedCard?.pmId === pmId
              ? 'bg-SeabiscuitMainThemeColor/5 border-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor'
              : 'border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor',
            paymentRequestStatus === 'approved'
              ? 'group cursor-pointer hover:bg-SeabiscuitMainThemeColor/5 hover:border-SeabiscuitMainThemeColor/5 hover:text-SeabiscuitMainThemeColor'
              : 'bg-black/[2%] cursor-not-allowed'
          )}
          onClick={() => {
            if (paymentRequestStatus === 'approved') {
              setSelectedCard({ type: type, pmId: pmId, cardNumber: cardNumber })
            }
          }}
        >
          <div className="flex items-center">
            {type === 'card' && (
              <>
                <IconCreditCard className={iconClasses} />
                <span className="opacity-50">XXXX-XXXX-XXXX-</span>
              </>
            )}
            {type === 'us_bank_account' && (
              <>
                <IconBank className={iconClasses} />
                <span>{bankName}, *</span>
              </>
            )}
            {cardNumber}
          </div>
          <div className="ml-auto flex items-center gap-4">
            {isDefault && (
              <span className="leading-none text-[10px] py-[6px] px-[10px] border border-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor rounded-full">
                Default
              </span>
            )}
            {paymentRequestStatus !== 'pending' &&
              paymentRequestStatus !== 'approved' &&
              paymentRequestStatus !== 'rejected' && (
                <SendRequest size="lg" user={user} card={card} organizer={organiserProfile} />
              )}
            {paymentRequestStatus === 'pending' && <PendingRequest size="lg" user={user} />}
            {paymentRequestStatus === 'rejected' && <RejectedRequest size="lg" user={user} />}
            <Button
              variant="ghost"
              disabled={selectedCard?.pmId === pmId || trashLoading}
              size="lg"
              className={cn(
                'p-0 bg-transparent rounded-full group-hover:text-SeabiscuitMainThemeColor opacity-50 hover:opacity-100 transition-all',
                selectedCard?.pmId === pmId
                  ? 'hidden text-SeabiscuitMainThemeColor'
                  : 'text-SeabiscuitDark200ThemeColor'
              )}
              onClick={async () => await onRemoveCard(pmId)}
            >
              {trashLoading ? <Loader2Icon className="animate-spin" /> : <Trash2 />}
            </Button>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent hidden={paymentRequestStatus === 'approved'}>
        You don't have access please send request
      </TooltipContent>
    </Tooltip>
  )
}
